import { detectBibleReference } from "./bibleReference.js";
import { getSearchSuggestions } from "./siteSearch.js";

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

export function makeBestMatch({
  passedPossibilities,
  scriptureResults,
  peopleResults,
  filteredPageResults,
  verseUrl = (ref) => `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=NASB`,
}) {
  if (passedPossibilities?.people?.person) {
    return {
      type: "person",
      label: "Best Match",
      title: passedPossibilities.people.person.name,
      subtitle: passedPossibilities.people.person.title || "Person",
      meta: `Matched on ${passedPossibilities.people.matchedOn || "search"}${
        passedPossibilities.people.score
          ? ` • Score ${passedPossibilities.people.score}`
          : ""
      }`,
      to: `/people/${passedPossibilities.people.person.id}`,
      testId: "search-result-person",
    };
  }

  if (passedPossibilities?.pages?.route) {
    return {
      type: "page",
      label: "Best Match",
      title: passedPossibilities.pages.title,
      subtitle: "Page",
      meta: passedPossibilities.pages.score
        ? `Score ${passedPossibilities.pages.score}`
        : "Top page result",
      to: passedPossibilities.pages.route,
      testId: "search-result-page",
    };
  }

  if (scriptureResults.length > 0) {
    const reference = scriptureResults[0];
    return {
      type: "scripture",
      label: "Best Match",
      title: reference,
      subtitle: "Scripture",
      meta: "Open verse lookup",
      href: verseUrl(reference),
    };
  }

  if (peopleResults.length > 0) {
    const person = peopleResults[0];
    return {
      type: "person",
      label: "Best Match",
      title: person.title,
      subtitle: person.subtitle || "Person",
      meta: "Top person result",
      to: person.route,
      testId: "search-result-person",
    };
  }

  if (filteredPageResults.length > 0) {
    const page = filteredPageResults[0];
    return {
      type: "page",
      label: "Best Match",
      title: page.title,
      subtitle: page.subtitle || "Page",
      meta: "Top page result",
      to: page.route,
      testId: "search-result-page",
    };
  }

  return null;
}

export function getMoreResults({
  bestMatch,
  scriptureResults,
  peopleResults,
  filteredPageResults,
  verseUrl = (ref) => `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=NASB`,
}) {
  const bestTitle = normalizeText(bestMatch?.title);
  const bestTo = normalizeText(bestMatch?.to);
  const bestHref = normalizeText(bestMatch?.href);

  return {
    scripture: scriptureResults.filter((reference) => {
      const href = verseUrl(reference);
      return (
        normalizeText(reference) !== bestTitle &&
        normalizeText(href) !== bestHref
      );
    }),
    people: peopleResults.filter((item) => {
      return (
        normalizeText(item.title) !== bestTitle &&
        normalizeText(item.route) !== bestTo
      );
    }),
    pages: filteredPageResults.filter((item) => {
      return (
        normalizeText(item.title) !== bestTitle &&
        normalizeText(item.route) !== bestTo
      );
    }),
  };
}

/*
 * `getPeopleSuggestions` is passed in rather than imported.
 *
 * Importing it here bound this module to peopleIndex.json, and pageNotFound
 * imports this module -- so the 404 page, which App.js keeps eager because the
 * person and battle pages render it inline, dragged all 538 kB of the people
 * corpus into the main bundle for every visitor on every route.
 *
 * Callers that already hold the corpus pass their own; callers still waiting
 * on it pass nothing and get pages and scripture until it lands.
 */
export function buildSearchResults(
  query,
  passedPossibilities = null,
  verseUrl = null,
  getPeopleSuggestions = null
) {
  const trimmed = String(query || "").trim();

  if (!trimmed) {
    return {
      scriptureResults: [],
      peopleResults: [],
      pageResults: [],
      filteredPageResults: [],
      bestMatch: null,
      moreResults: { scripture: [], people: [], pages: [] },
      hasResults: false,
    };
  }

  const scriptureMatch = detectBibleReference(trimmed);
  const scriptureResults = scriptureMatch ? [scriptureMatch] : [];
  const peopleResults = getPeopleSuggestions
    ? getPeopleSuggestions(trimmed, 20)
    : [];
  const pageResults = getSearchSuggestions(trimmed, 20);

  const filteredPageResults = pageResults.filter((page) => {
    return !peopleResults.some(
      (person) =>
        person.route === page.route ||
        String(person.title || "").trim().toLowerCase() ===
          String(page.title || "").trim().toLowerCase()
    );
  });

  const bestMatch = verseUrl
    ? makeBestMatch({
        passedPossibilities,
        scriptureResults,
        peopleResults,
        filteredPageResults,
        verseUrl,
      })
    : makeBestMatch({
        passedPossibilities,
        scriptureResults,
        peopleResults,
        filteredPageResults,
      });

  const moreResults = verseUrl
    ? getMoreResults({
        bestMatch,
        scriptureResults,
        peopleResults,
        filteredPageResults,
        verseUrl,
      })
    : getMoreResults({
        bestMatch,
        scriptureResults,
        peopleResults,
        filteredPageResults,
      });

  const hasResults =
    scriptureResults.length > 0 ||
    peopleResults.length > 0 ||
    filteredPageResults.length > 0 ||
    !!passedPossibilities?.people?.person ||
    !!passedPossibilities?.pages;

  return {
    scriptureResults,
    peopleResults,
    pageResults,
    filteredPageResults,
    bestMatch,
    moreResults,
    hasResults,
  };
}